import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, Calendar, Shield, FileText } from "lucide-react";
import Logo from "../../components/Logo";

export default function TenantProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/public/tenant/${slug}`)
      .then((r) => r.json())
      .then((d) => { setProfile(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
  if (!profile || profile.detail) return <div className="min-h-screen flex items-center justify-center text-gray-400">Profile not found.</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="px-6 py-5 bg-white border-b">
        <Link to="/"><Logo size="sm" /></Link>
      </header>
      <div className="max-w-lg mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
          
          {/* Avatar and name */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-[#F97316] text-white flex items-center justify-center text-2xl font-bold">
              {profile.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">{profile.name}</h1>
                {profile.kyc_verified && <BadgeCheck className="h-5 w-5 text-[#F97316]" />}
              </div>
              {profile.city && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {profile.city}
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-[#F97316]">{profile.completed_rentals}</div>
              <div className="text-xs text-gray-500 mt-0.5">Completed</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-[#F97316]">{profile.rating?.avg ?? "—"}</div>
              <div className="text-xs text-gray-500 mt-0.5">Rating</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-[#F97316]">{profile.rating?.count ?? 0}</div>
              <div className="text-xs text-gray-500 mt-0.5">Reviews</div>
            </div>
          </div>

          {/* Preferences */}
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-700">Preferences</h2>
            <div className="flex flex-wrap gap-2">
              {profile.deposit_protection && (
                <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs">
                  <Shield className="h-3 w-3" /> Deposit Protection
                </span>
              )}
              {profile.e_agreement && (
                <span className="flex items-center gap-1 px-3 py-1 bg-[#FFF4ED] text-indigo-900 border border-indigo-200 rounded-full text-xs">
                  <FileText className="h-3 w-3" /> E-Agreement
                </span>
              )}
              {profile.budget_min && profile.budget_max && (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                  Budget Rs {profile.budget_min.toLocaleString("en-IN")} – {profile.budget_max.toLocaleString("en-IN")}
                </span>
              )}
            </div>
          </div>

          {/* Member since */}
          <div className="flex items-center gap-2 text-xs text-gray-400 pt-2 border-t">
            <Calendar className="h-3.5 w-3.5" />
            Member since {profile.member_since}
          </div>

        </div>
      </div>
    </div>
  );
}